'use client';

import { useEffect, useMemo, useState } from 'react';
import { format, type Locale } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { AlertTriangle, Power, RefreshCw, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useAccountStatus,
  useLiveSubscriptionCount,
  useUpdateAccountStatus,
  type UpdateAccountStatusInput,
} from '@/lib/api/account-manager';

interface AccountStatusTabProps {
  accountId: string | undefined;
}

type StatusUpdate = Omit<UpdateAccountStatusInput, 'subscriptionAction'>;

type LocalStatus = {
  billingDisabled: boolean;
  suspendCrmAccess: boolean;
  reason: string;
};

const useDateLocale = () => {
  const { i18n } = useTranslation('common');
  return i18n.language?.startsWith('fr') ? fr : enUS;
};

export function AccountStatusTab({ accountId }: AccountStatusTabProps) {
  const { t } = useTranslation('common');
  const dateLocale = useDateLocale();
  const { data: status, isLoading, refetch } = useAccountStatus(accountId);
  const updateStatus = useUpdateAccountStatus(() => refetch());

  const [pendingSuspension, setPendingSuspension] = useState<StatusUpdate | null>(null);
  const liveSubscriptions = useLiveSubscriptionCount(accountId, pendingSuspension !== null);

  const [localStatus, setLocalStatus] = useState<LocalStatus>({
    billingDisabled: false,
    suspendCrmAccess: false,
    reason: '',
  });

  useEffect(() => {
    if (!status) return;
    setLocalStatus({
      billingDisabled: status.billingDisabled,
      suspendCrmAccess: status.suspendCrmAccess,
      reason: status.audit?.reason ?? '',
    });
  }, [status]);

  const hasChanges = useMemo(() => {
    if (!status) return false;
    return (
      localStatus.billingDisabled !== status.billingDisabled ||
      localStatus.suspendCrmAccess !== status.suspendCrmAccess ||
      localStatus.reason !== (status.audit?.reason ?? '')
    );
  }, [localStatus, status]);

  const isSuspended = !!status?.billingDisabled && !!status?.suspendCrmAccess;

  const submit = (update: StatusUpdate) => {
    if (update.billingDisabled && update.suspendCrmAccess && !isSuspended) {
      setPendingSuspension(update);
      return;
    }
    updateStatus.mutate(update);
  };

  const confirmSuspension = (subscriptionAction: 'pause' | 'offboard') => {
    if (!pendingSuspension) return;
    updateStatus.mutate({ ...pendingSuspension, subscriptionAction });
    setPendingSuspension(null);
  };

  const handleSave = () => {
    if (!accountId) return;
    submit({
      accountId,
      billingDisabled: localStatus.billingDisabled,
      suspendCrmAccess: localStatus.suspendCrmAccess,
      reason: localStatus.reason.trim() || undefined,
    });
  };

  const handleDisableAccount = () => {
    if (!accountId) return;
    submit({
      accountId,
      billingDisabled: true,
      suspendCrmAccess: true,
      reason: localStatus.reason.trim() || t('accountManager.accountStatus.defaultDisableReason'),
    });
  };

  const handleOffboardAccount = () => {
    if (!accountId) return;
    setPendingSuspension({
      accountId,
      billingDisabled: true,
      suspendCrmAccess: true,
      reason: localStatus.reason.trim() || undefined,
    });
  };

  const handleReEnableAccount = () => {
    if (!accountId) return;
    updateStatus.mutate({
      accountId,
      billingDisabled: false,
      suspendCrmAccess: false,
      reason: localStatus.reason.trim() || undefined,
    });
  };

  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('accountManager.accountStatus.loadingAccount')}
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !status) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-full max-w-xl" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Power className="h-5 w-5" />
              {t('accountManager.accountStatus.title')}
            </CardTitle>
            <CardDescription className="mt-2 max-w-2xl">
              {t('accountManager.accountStatus.description')}
            </CardDescription>
          </div>
          <StatusBadge
            billingDisabled={status.billingDisabled}
            suspended={status.suspendCrmAccess}
          />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{t('accountManager.accountStatus.whatDisableDoes')}</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  <li>{t('accountManager.accountStatus.effects.subscriptions')}</li>
                  <li>{t('accountManager.accountStatus.effects.stopBilling')}</li>
                  <li>{t('accountManager.accountStatus.effects.stopEmails')}</li>
                  <li>{t('accountManager.accountStatus.effects.clearBlocks')}</li>
                  <li>{t('accountManager.accountStatus.effects.keepData')}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <StatusToggle
              label={t('accountManager.accountStatus.billingDisabledLabel')}
              description={t('accountManager.accountStatus.billingDisabledDescription')}
              checked={localStatus.billingDisabled}
              onCheckedChange={(billingDisabled) =>
                setLocalStatus((prev) => ({ ...prev, billingDisabled }))
              }
              disabled={updateStatus.isPending}
              critical={localStatus.billingDisabled}
              onLabel={t('accountManager.accountStatus.toggleOn')}
            />
            <StatusToggle
              label={t('accountManager.accountStatus.suspendCrmLabel')}
              description={t('accountManager.accountStatus.suspendCrmDescription')}
              checked={localStatus.suspendCrmAccess}
              onCheckedChange={(suspendCrmAccess) =>
                setLocalStatus((prev) => ({ ...prev, suspendCrmAccess }))
              }
              disabled={updateStatus.isPending}
              critical={localStatus.suspendCrmAccess}
              onLabel={t('accountManager.accountStatus.toggleOn')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-status-reason">
              {t('accountManager.accountStatus.reasonLabel')}
            </Label>
            <Textarea
              id="account-status-reason"
              value={localStatus.reason}
              onChange={(event) =>
                setLocalStatus((prev) => ({ ...prev, reason: event.target.value }))
              }
              placeholder={t('accountManager.accountStatus.reasonPlaceholder')}
              rows={3}
              disabled={updateStatus.isPending}
            />
          </div>

          {status.audit && (status.audit.disabledAt || status.audit.enabledAt) && (
            <AuditTrail audit={status.audit} dateLocale={dateLocale} />
          )}

          <div className="flex flex-wrap items-center gap-2">
            {hasChanges ? (
              <Button onClick={handleSave} disabled={updateStatus.isPending}>
                {updateStatus.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    {t('accountManager.accountStatus.saving')}
                  </>
                ) : (
                  t('accountManager.accountStatus.saveChanges')
                )}
              </Button>
            ) : status.billingDisabled || status.suspendCrmAccess ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleReEnableAccount}
                  disabled={updateStatus.isPending}
                >
                  {t('accountManager.accountStatus.reEnableAccount')}
                </Button>
                {isSuspended && (
                  <Button
                    variant="destructive"
                    onClick={handleOffboardAccount}
                    disabled={updateStatus.isPending}
                  >
                    {t('accountManager.accountStatus.offboardAccount')}
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="destructive"
                onClick={handleDisableAccount}
                disabled={updateStatus.isPending}
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                {t('accountManager.accountStatus.disableAccount')}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {hasChanges
              ? t('accountManager.accountStatus.hintHasChanges')
              : t('accountManager.accountStatus.hintQuickAction')}
          </p>
        </CardContent>
      </Card>

      <AlertDialog
        open={pendingSuspension !== null}
        onOpenChange={(open) => !open && setPendingSuspension(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('accountManager.accountStatus.suspendDialog.title')}
            </AlertDialogTitle>
            {liveSubscriptions.isError && (
              <AlertDialogDescription>
                {t('accountManager.accountStatus.suspendDialog.countFailed')}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border p-3">
              <p className="font-medium">{t('accountManager.accountStatus.suspendDialog.pauseTitle')}</p>
              <p className="mt-1 text-muted-foreground">
                {liveSubscriptions.data
                  ? t('accountManager.accountStatus.suspendDialog.pauseDescription', {
                      count: liveSubscriptions.data.pausable,
                    })
                  : t('accountManager.accountStatus.suspendDialog.loadingCount')}
              </p>
            </div>
            <div className="rounded-lg border border-destructive/40 p-3">
              <p className="font-medium text-destructive">
                {t('accountManager.accountStatus.suspendDialog.offboardTitle')}
              </p>
              <p className="mt-1 text-muted-foreground">
                {liveSubscriptions.data
                  ? t('accountManager.accountStatus.suspendDialog.offboardDescription', {
                      count: liveSubscriptions.data.cancellable,
                    })
                  : t('accountManager.accountStatus.suspendDialog.loadingCount')}
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('accountManager.accountStatus.suspendDialog.cancel')}</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => confirmSuspension('pause')}
              disabled={liveSubscriptions.isLoading}
            >
              {t('accountManager.accountStatus.suspendDialog.pauseButton')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmSuspension('offboard')}
              disabled={liveSubscriptions.isLoading}
            >
              {t('accountManager.accountStatus.suspendDialog.offboardButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusBadge({
  billingDisabled,
  suspended,
}: {
  billingDisabled: boolean;
  suspended: boolean;
}) {
  const { t } = useTranslation('common');

  if (billingDisabled && suspended) {
    return (
      <Badge variant="destructive">
        {t('accountManager.accountStatus.badges.billingOffCrmSuspended')}
      </Badge>
    );
  }
  if (billingDisabled) {
    return (
      <Badge variant="destructive">
        {t('accountManager.accountStatus.badges.billingDisabled')}
      </Badge>
    );
  }
  if (suspended) {
    return <Badge variant="yellow">{t('accountManager.accountStatus.badges.crmSuspended')}</Badge>;
  }
  return <Badge variant="green">{t('accountManager.accountStatus.badges.active')}</Badge>;
}

function StatusToggle({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  critical,
  onLabel,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled?: boolean;
  critical?: boolean;
  onLabel: string;
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
      </div>
      {critical && (
        <Badge variant="destructive" className="mt-3">
          {onLabel}
        </Badge>
      )}
    </div>
  );
}

function AuditTrail({
  audit,
  dateLocale,
}: {
  audit: NonNullable<import('@/lib/api/account-manager').AccountStatusOutput['audit']>;
  dateLocale: Locale;
}) {
  const { t } = useTranslation('common');
  const empty = t('accountManager.accountStatus.audit.empty');
  const formatDate = (value: string) =>
    format(new Date(value), 'PPp', { locale: dateLocale });

  return (
    <div className="rounded-lg border bg-muted/30 p-4 text-sm">
      <p className="font-medium">{t('accountManager.accountStatus.audit.title')}</p>
      <dl className="mt-2 grid gap-2 sm:grid-cols-2">
        {audit.disabledAt && (
          <>
            <dt className="text-muted-foreground">
              {t('accountManager.accountStatus.audit.disabledAt')}
            </dt>
            <dd>{formatDate(audit.disabledAt)}</dd>
            <dt className="text-muted-foreground">
              {t('accountManager.accountStatus.audit.disabledBy')}
            </dt>
            <dd>{audit.disabledBy ?? empty}</dd>
          </>
        )}
        {audit.enabledAt && (
          <>
            <dt className="text-muted-foreground">
              {t('accountManager.accountStatus.audit.enabledAt')}
            </dt>
            <dd>{formatDate(audit.enabledAt)}</dd>
            <dt className="text-muted-foreground">
              {t('accountManager.accountStatus.audit.enabledBy')}
            </dt>
            <dd>{audit.enabledBy ?? empty}</dd>
          </>
        )}
        {audit.reason && (
          <>
            <dt className="text-muted-foreground">
              {t('accountManager.accountStatus.audit.reason')}
            </dt>
            <dd className="sm:col-span-1">{audit.reason}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
